import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Typography,
  Container,
  Button,
  Paper,
  Box,
} from '@mui/material';
import { useTranslation } from '../common/components/LocalizationProvider';
import PageLayout from '../common/components/PageLayout';
import SettingsMenu from './components/SettingsMenu';
import { useCatch } from '../reactHelper';
import { useAttributePreference } from '../common/util/preferences';
import { distanceFromMeters, distanceToMeters, distanceUnitString } from '../common/util/converter';
import useSettingsStyles from './common/useSettingsStyles';
import fetchOrThrow from '../common/util/fetchOrThrow';
import { usePremiumTokens } from '../common/theme/usePremiumTokens';
import PremiumInput from '../common/components/premium/PremiumInput';

const AccumulatorsPage = () => {
  const navigate = useNavigate();
  const { classes } = useSettingsStyles();
  const t = useTranslation();
  const tokens = usePremiumTokens();

  const distanceUnit = useAttributePreference('distanceUnit');

  const { deviceId } = useParams();
  const position = useSelector((state) => state.session.positions[deviceId]);

  const [item, setItem] = useState();

  useEffect(() => {
    if (position) {
      setItem({
        deviceId: parseInt(deviceId, 10),
        hours: position.attributes.hours || 0,
        totalDistance: position.attributes.totalDistance || 0,
      });
    }
  }, [deviceId, position]);

  const handleSave = useCatch(async () => {
    await fetchOrThrow(`/api/devices/${deviceId}/accumulators`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(item),
    });
    navigate(-1);
  });

  return (
    <PageLayout menu={<SettingsMenu />} breadcrumbs={['sharedDeviceAccumulators']}>
      {item && (
        <Container maxWidth={false} className={classes.container}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: tokens.radius.lg,
              backgroundColor: tokens.surface.card,
              border: `1px solid ${tokens.border.subtle}`,
            }}
          >
            <Typography variant="h6" sx={{ mb: 3, color: tokens.text.primary }}>
              {t('sharedRequired')}
            </Typography>

            <Box display="flex" flexDirection="column" gap={2}>
              <Box
                display="flex"
                width="100%"
                gap={2}
                sx={{ flexDirection: { xs: 'column', sm: 'row' } }}
              >
                <Box flex={1} minWidth={0}>
                  <PremiumInput
                    type="number"
                    value={item.hours / 3600000}
                    onChange={(event) => setItem({ ...item, hours: Number(event.target.value) * 3600000 })}
                    label={t('positionHours')}
                  />
                </Box>
                <Box flex={1} minWidth={0}>
                  <PremiumInput
                    type="number"
                    value={distanceFromMeters(item.totalDistance, distanceUnit)}
                    onChange={(event) => setItem({ ...item, totalDistance: distanceToMeters(Number(event.target.value), distanceUnit) })}
                    label={`${t('deviceTotalDistance')} (${distanceUnitString(distanceUnit, t)})`}
                  />
                </Box>
              </Box>
            </Box>
          </Paper>

          <div className={classes.buttons}>
            <Button
              type="button"
              color="primary"
              variant="outlined"
              onClick={() => navigate(-1)}
              sx={{
                borderRadius: tokens.radius.md,
                borderColor: tokens.border.default,
                '&:hover': { borderColor: tokens.accent.primary },
              }}
            >
              {t('sharedCancel')}
            </Button>
            <Button
              type="button"
              color="primary"
              variant="contained"
              onClick={handleSave}
              sx={{
                borderRadius: tokens.radius.md,
                backgroundColor: tokens.accent.primary,
                '&:hover': { backgroundColor: tokens.accent.hover },
              }}
            >
              {t('sharedSave')}
            </Button>
          </div>
        </Container>
      )}
    </PageLayout>
  );
};

export default AccumulatorsPage;
